import type { ReactNode } from 'react';
import { cn } from '@rocco/utils/cn';

interface IslandTooltipProps {
    /** Label shown above the control. */
    label: string;
    /** Keep the tooltip up regardless of hover (e.g. after "Copied"). */
    forceVisible?: boolean;
    /** The control the tooltip belongs to. */
    children: ReactNode;
}

/**
 * Label for a control inside the staff island.
 *
 * Deliberately not Aria's MessageTooltip: that positions through a portalled
 * Popper, and the island is `position: fixed`, so the popper resolved against
 * the document rather than the viewport and left the label in the page corner.
 * This one is a sibling of the button inside the same fixed container, so it
 * needs no measuring at all — the label cannot land anywhere but above its
 * control.
 *
 * Hover and keyboard focus both raise it, so the label is reachable without a
 * pointer. It is a visual affordance only — the control names itself with an
 * `aria-label`, since every icon here is `aria-hidden`.
 */
export const IslandTooltip = ({
    label,
    forceVisible = false,
    children,
}: IslandTooltipProps) => (
    <div className="group/tip relative flex">
        {children}
        <span
            // Decorative: the control it belongs to carries the same text as
            // its `aria-label`, and the span is always mounted (opacity alone
            // hides it), so leaving it exposed reads every label on the page
            // out as loose text.
            aria-hidden="true"
            className={cn(
                // Centred on the control and lifted clear of the island's
                // padding. translate-x-[-50%] pairs with left-1/2 so the label
                // stays centred whatever its length.
                'pointer-events-none absolute bottom-[calc(100%+0.75rem)] left-1/2 -translate-x-1/2',
                // Wraps rather than running off the screen. The island sits at
                // `left-8`, so a centred nowrap label spills past the left edge
                // of the viewport — and the longest label here is the one
                // explaining why the visibility control is locked, which is
                // exactly the one that has to be readable. `w-max` keeps short
                // labels on one line; the cap only bites on the long ones.
                'w-max max-w-[18rem] rounded-md bg-[#141414] px-3 py-1.5 text-center text-[0.8125rem] leading-snug text-white',
                'transition-[opacity,transform] duration-150 ease-out',
                forceVisible
                    ? 'translate-y-0 opacity-100'
                    : cn(
                          'translate-y-0.5 opacity-0',
                          'group-hover/tip:translate-y-0 group-hover/tip:opacity-100',
                          'group-focus-within/tip:translate-y-0 group-focus-within/tip:opacity-100',
                      ),
            )}
        >
            {label}
        </span>
    </div>
);
